import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

const STEPS = [
  { id: 'connect', label: 'Conectando con el buzón' },
  { id: 'read', label: 'Leyendo correos' },
  { id: 'classify', label: 'Clasificando solicitudes' },
  { id: 'create', label: 'Creando expedientes' },
]

function CheckIcon({ size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function CrossIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

export function PrivacyIntakeProgress({ mailbox, running, outcome, error, onClose }) {
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (!running) return undefined
    setStep(0)
    const timer = setInterval(() => {
      setStep((current) => Math.min(current + 1, 2))
    }, 800)
    return () => clearInterval(timer)
  }, [running])

  useEffect(() => {
    if (running) return
    if ((outcome?.created || 0) > 0) setStep(3)
    else if (outcome || error) setStep(2)
  }, [running, outcome, error])

  if (!running && !outcome && !error) return null

  const done = !running
  const failed = Boolean(error) && !(outcome?.created > 0)
  const scanned = outcome?.scanned ?? 0
  const created = outcome?.created ?? 0
  const skipped = outcome?.skipped ?? 0
  const codes = outcome?.createdCodes || []
  const notes = (outcome?.skipNotes || []).slice(0, 4)

  return createPortal(
    <div className="privacy-intake-overlay" role="dialog" aria-modal="true" aria-label="Lectura del buzón">
      <div className="privacy-intake-panel">
        <div className={`privacy-intake-pulse${done ? ' is-done' : ''}${failed ? ' is-failed' : ''}`}>
          {!done ? (
            <>
              <span className="privacy-intake-ring" />
              <span className="privacy-intake-ring delay" />
            </>
          ) : (
            <span className="privacy-intake-badge" aria-hidden>
              {failed ? <CrossIcon /> : <CheckIcon size={22} />}
            </span>
          )}
        </div>
        <p className="privacy-intake-kicker">{done ? (failed ? 'No se pudo leer' : 'Lectura terminada') : 'Leyendo buzón'}</p>
        <h2 className="privacy-intake-mailbox">{mailbox || '—'}</h2>

        <ol className="privacy-intake-steps">
          {STEPS.map((item, index) => {
            const active = !done && index === step
            const complete = done ? !failed || index < 2 : index < step
            const skippedCreate = done && item.id === 'create' && created === 0 && !failed
            const ticked = complete || skippedCreate
            return (
              <li
                key={item.id}
                className={`privacy-intake-step${active ? ' is-active' : ''}${ticked ? ' is-complete' : ''}${skippedCreate ? ' is-skipped' : ''}`}
              >
                <span className="privacy-intake-dot">
                  {active ? <span className="privacy-intake-dot-spin" /> : null}
                  {ticked ? <CheckIcon /> : null}
                </span>
                <span>
                  {skippedCreate ? 'Sin expedientes nuevos' : item.label}
                  {item.id === 'read' && done && !failed ? ` · ${scanned}` : ''}
                  {item.id === 'create' && created > 0 ? ` · ${created}` : ''}
                </span>
              </li>
            )
          })}
        </ol>

        {done ? (
          <div className="privacy-intake-summary">
            {failed ? (
              <p className="privacy-intake-error">{error}</p>
            ) : created > 0 ? (
              <>
                <p>
                  Se {created === 1 ? 'abrió' : 'abrieron'}{' '}
                  <strong>
                    {created} {created === 1 ? 'solicitud' : 'solicitudes'}
                  </strong>
                  {skipped ? ` · ${skipped} correo(s) omitido(s)` : ''}.
                </p>
                {codes.length ? (
                  <p className="privacy-intake-codes">{codes.join(' · ')}</p>
                ) : null}
              </>
            ) : scanned === 0 ? (
              <p>No había correos nuevos en el buzón.</p>
            ) : (
              <p>
                Se leyeron {scanned} correo(s). Ninguno era una solicitud ARCO
                {skipped ? ` (${skipped} omitido(s))` : ''}.
              </p>
            )}
            {notes.length && !failed ? (
              <ul className="privacy-intake-notes">
                {notes.map((note, index) => (
                  <li key={`${note}-${index}`}>{note}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <p className="privacy-intake-hint">Sólo se crea un caso si la IA reconoce un derecho ARCO.</p>
        )}

        {done ? (
          <button className="btn btn-primary" type="button" onClick={onClose}>
            Entendido
          </button>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
